type CheckState = 'checked' | 'unchecked' | 'indeterminate';

function setCheckState(input: HTMLInputElement, state: CheckState): void {
    const wrap = input.closest('.nais-role-privileges__check');
    input.indeterminate = state === 'indeterminate';
    input.checked = state === 'checked';
    if (state === 'indeterminate') {
        input.setAttribute('indeterminate', 'true');
    } else {
        input.removeAttribute('indeterminate');
    }
    wrap?.classList.toggle('is-indeterminate', state === 'indeterminate');
}

function resolveState(inputs: HTMLInputElement[]): CheckState {
    const checkedCount = inputs.filter((input) => input.checked).length;

    if (checkedCount === 0) {
        return 'unchecked';
    }

    if (checkedCount === inputs.length) {
        return 'checked';
    }

    return 'indeterminate';
}

function getActionInputs(root: HTMLElement, module: string, presenter?: string): HTMLInputElement[] {
    const selector = presenter
        ? `.nais-role-privileges__input--action[data-module="${module}"][data-presenter="${presenter}"]`
        : `.nais-role-privileges__input--action[data-module="${module}"]`;

    return Array.from(root.querySelectorAll<HTMLInputElement>(selector));
}

function updatePresenterState(root: HTMLElement, module: string, presenter: string): void {
    const presenterInput = root.querySelector<HTMLInputElement>(
        `.nais-role-privileges__input--presenter[data-module="${module}"][data-presenter="${presenter}"]`,
    );
    if (!presenterInput) {
        return;
    }

    const actions = getActionInputs(root, module, presenter);
    setCheckState(presenterInput, resolveState(actions));
}

function updateModuleState(root: HTMLElement, module: string): void {
    const moduleInput = root.querySelector<HTMLInputElement>(
        `.nais-role-privileges__input--module[data-module="${module}"]`,
    );
    if (!moduleInput) {
        return;
    }

    const actions = getActionInputs(root, module);
    setCheckState(moduleInput, resolveState(actions));
}

function bindCascade(root: HTMLElement): void {
    root.querySelectorAll<HTMLInputElement>('.nais-role-privileges__input--module').forEach((moduleInput) => {
        const module = moduleInput.dataset.module ?? '';
        moduleInput.addEventListener('change', () => {
            const checked = moduleInput.checked;
            getActionInputs(root, module).forEach((actionInput) => {
                actionInput.checked = checked;
                actionInput.indeterminate = false;
                actionInput.removeAttribute('indeterminate');
            });
            root.querySelectorAll<HTMLInputElement>(
                `.nais-role-privileges__input--presenter[data-module="${module}"]`,
            ).forEach((presenterInput) => {
                setCheckState(presenterInput, checked ? 'checked' : 'unchecked');
            });
            setCheckState(moduleInput, checked ? 'checked' : 'unchecked');
        });
    });

    root.querySelectorAll<HTMLInputElement>('.nais-role-privileges__input--presenter').forEach((presenterInput) => {
        const module = presenterInput.dataset.module ?? '';
        const presenter = presenterInput.dataset.presenter ?? '';
        presenterInput.addEventListener('change', () => {
            const checked = presenterInput.checked;
            getActionInputs(root, module, presenter).forEach((actionInput) => {
                actionInput.checked = checked;
                actionInput.indeterminate = false;
                actionInput.removeAttribute('indeterminate');
            });
            setCheckState(presenterInput, checked ? 'checked' : 'unchecked');
            updateModuleState(root, module);
        });
    });

    root.querySelectorAll<HTMLInputElement>('.nais-role-privileges__input--action').forEach((actionInput) => {
        const module = actionInput.dataset.module ?? '';
        const presenter = actionInput.dataset.presenter ?? '';
        actionInput.addEventListener('change', () => {
            updatePresenterState(root, module, presenter);
            updateModuleState(root, module);
        });
    });
}

function bindSearch(root: HTMLElement): void {
    const searchInput = root.querySelector<HTMLInputElement>('#naisRolePrivilegesSearch');
    if (!searchInput) {
        return;
    }

    searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim().toLowerCase();

        root.querySelectorAll<HTMLElement>('.nais-role-privileges__module').forEach((moduleEl) => {
            const haystack = (moduleEl.dataset.searchText ?? moduleEl.textContent ?? '').toLowerCase();
            moduleEl.classList.toggle('is-filtered-out', query !== '' && !haystack.includes(query));
        });
    });
}

export function initRolesAddEdit(root: HTMLElement): void {
    const privilegesRoot = root.querySelector<HTMLElement>('.nais-role-privileges');
    if (!privilegesRoot) {
        return;
    }

    if (privilegesRoot.dataset.naisRolesPrivilegesBound !== '1') {
        privilegesRoot.dataset.naisRolesPrivilegesBound = '1';
        bindCascade(privilegesRoot);
        bindSearch(privilegesRoot);
    }

    // Compute initial parent states from server-rendered `checked` (partial → indeterminate).
    privilegesRoot.querySelectorAll<HTMLInputElement>('.nais-role-privileges__input--presenter').forEach((input) => {
        updatePresenterState(privilegesRoot, input.dataset.module ?? '', input.dataset.presenter ?? '');
    });
    privilegesRoot.querySelectorAll<HTMLInputElement>('.nais-role-privileges__input--module').forEach((input) => {
        updateModuleState(privilegesRoot, input.dataset.module ?? '');
    });
}
